/**
 * CameraController — Manages multiple camera viewpoints over the 3D field.
 *
 * Supported camera ids (any subset can be enabled per scene via the manifest's
 * camera_options list):
 *
 *   top_down          Orthographic, full-field overhead. The default for
 *                     coverage / play diagram review.
 *   qb_perspective    First-person perspective at the QB's eye height (1.85m),
 *                     ~2 yards behind the QB, looking downfield.
 *   sideline          Perspective from the middle of the home sideline at
 *                     coach height (~3m).
 *   broadcast         Perspective from high above the home end zone corner,
 *                     mimics the standard TV All-22 angle.
 *   end_zone          Low perspective from behind the home end zone.
 *   follow_ball       Chase camera that trails the ball position (set via
 *                     setBallPosition) at config.height/distance behind it.
 *   follow_player     Chase camera that trails an arbitrary actor (target id
 *                     set via setFollowTarget). Falls back to QB if unset.
 *
 * Camera switches are immediate (no transitions) — tween cameras feel laggy
 * when alternating between perspectives during play breakdown. The renderer
 * just queries `activeCamera` and renders.
 *
 * NOTE: the follow_* poses recompute every frame in `tick()` so the chase
 * cameras smoothly track moving entities; static poses are recomputed only
 * when their reference point changes.
 */

import * as THREE from 'three';

const _DEFAULT_FOLLOW = {
  height_m: 5.0,
  distance_m: 7.0,
};

const _VALID_ORIENTATIONS = new Set(['landscape', 'portrait']);

export default class CameraController {
  constructor(viewport, aspect, orientation = 'landscape') {
    this.vp = viewport || { width: 100, height: 53.3 };
    this.aspect = aspect;
    this.orientation = _VALID_ORIENTATIONS.has(orientation) ? orientation : 'landscape';
    this.activeCameraId = 'top_down';
    this.qbPosition = new THREE.Vector3(35, 0, this.vp.height / 2);
    this.ballPosition = new THREE.Vector3(35, 0, this.vp.height / 2);
    this.followTargetPosition = new THREE.Vector3(35, 0, this.vp.height / 2);
    this.followTargetActorId = null;
    this.followConfig = { ..._DEFAULT_FOLLOW };

    this._buildOrtho();
    this._buildPerspective();
    this._updateForId('top_down');
  }

  _buildOrtho() {
    // Top-down orthographic frustum.
    //
    // Pick whichever axis the field is wider in and derive the other
    // from the screen aspect so the entire field fits without
    // distortion.
    //
    // Margin bumped from 4 → 10 → 14yd (2026-05-02) after the coach
    // reported "the top down view still has things cut off, you may
    // need to zoom out a bit more". The wider margin guarantees the
    // sideline UI overlays (camera-select dropdown, jersey badges)
    // never occlude players and keeps the deepest defenders (FS/SS
    // ~14 yards behind the LOS) inside the visible frame even on
    // narrow editor canvases with the right-hand sidebar open.
    //
    // Orientation handling: `up` is rotated when in portrait mode so
    // world +x (downfield) maps to screen +y (offense moves UP the
    // screen — playbook convention). The frustum half-extents are
    // swapped via _orthoFrustum() so the long field axis fits
    // vertically. Camera position / lookAt are unchanged because the
    // field mesh and actors stay in their canonical world coords.
    const { halfW, halfH } = this._orthoFrustum(this.aspect);
    this.ortho = new THREE.OrthographicCamera(-halfW, halfW, halfH, -halfH, 0.1, 400);
    this.ortho.position.set(this.vp.width / 2, 80, this.vp.height / 2);
    this.ortho.lookAt(this.vp.width / 2, 0, this.vp.height / 2);
    this._applyOrthoUp();
  }

  _applyOrthoUp() {
    if (this.orientation === 'portrait') {
      // Camera y-axis aligns with world +x → downfield is screen-up.
      // Coaches see the playbook view: end zone at top, offense
      // moving up the page.
      this.ortho.up.set(1, 0, 0);
    } else {
      // Legacy landscape: sideline at z=0 sits at the top of the
      // screen (broadcast view).
      this.ortho.up.set(0, 0, -1);
    }
    // Three.js needs lookAt() re-applied after `up` changes for the
    // matrix to reflect the new orientation; otherwise the frustum
    // rotates but the camera basis stays stale.
    this.ortho.lookAt(this.vp.width / 2, 0, this.vp.height / 2);
    this.ortho.updateMatrixWorld();
  }

  _orthoFrustum(aspect) {
    // Coach reported "the top down view is cutting off the bottom WRs."
    // Root cause: the editor canvas is squeezed by the right-hand
    // sidebar so its rendered aspect is much narrower than the
    // 100:53.3 field aspect. The previous logic computed the frustum
    // as `halfW = fieldW/2; halfH = halfW / screenAspect` whenever
    // the field was wider than the screen — fine when the canvas is
    // close to a 16:9 widescreen, but it ALSO fired when the canvas
    // was narrower than the field, in which case `halfH = halfW /
    // narrowAspect` shrinks the visible vertical bound below the
    // field height and clips the sideline players.
    //
    // Correct behaviour:
    //   canvas wider than field (e.g. coach preview modal at 16:9)
    //     → fit the field by HEIGHT; expand horizontally so left/right
    //       margins grow but top/bottom stay tight against the field.
    //   canvas narrower than field (e.g. editor with sidebar open)
    //     → fit the field by WIDTH; expand vertically so top/bottom
    //       margins grow and ALL 22 players including the bottom WRs
    //       stay in frame.
    //
    // Portrait mode swaps which world axis fills the frustum height
    // vs width — the camera's `up` is rotated 90° so world +x (the
    // 100yd downfield direction) maps to screen-up. We reflect that
    // by exposing the LONG field axis as fieldH and the short axis
    // as fieldW; the aspect-correction expansion logic below stays
    // identical.
    const margin = 14;
    const portrait = this.orientation === 'portrait';
    const fieldW = (portrait ? this.vp.height : this.vp.width) + margin * 2;
    const fieldH = (portrait ? this.vp.width : this.vp.height) + margin * 2;
    const fieldAspect = fieldW / fieldH;
    const screenAspect = aspect || 1.78;
    let halfW, halfH;
    if (screenAspect >= fieldAspect) {
      // Canvas wider than field — fit by height, pad horizontally.
      halfH = fieldH / 2;
      halfW = halfH * screenAspect;
    } else {
      // Canvas narrower than field — fit by width, pad vertically.
      halfW = fieldW / 2;
      halfH = halfW / screenAspect;
    }
    return { halfW, halfH };
  }

  _buildPerspective() {
    this.perspective = new THREE.PerspectiveCamera(60, this.aspect, 0.1, 600);
  }

  get activeCamera() {
    return this.activeCameraId === 'top_down' ? this.ortho : this.perspective;
  }

  switchCamera(cameraId, options = {}) {
    if (!cameraId) return;
    this.activeCameraId = cameraId;
    if (options.followConfig) {
      this.followConfig = { ..._DEFAULT_FOLLOW, ...options.followConfig };
    }
    this._updateForId(cameraId);
  }

  setQBPosition(x, z) {
    this.qbPosition.set(x, 0, z);
    // Treat the QB as the implicit ball-carrier when no explicit
    // ball entity is provided. This keeps `follow_ball` useful for
    // coaches reviewing dropback/throw mechanics even though our
    // current scenes don't author a ball trajectory.
    this.ballPosition.set(x, 0, z);
    if (this.activeCameraId === 'qb_perspective') {
      this._poseQBFirstPerson();
    } else if (this.activeCameraId === 'follow_ball') {
      this._poseFollowBall();
    }
  }

  setBallPosition(x, z) {
    this.ballPosition.set(x, 0, z);
    if (this.activeCameraId === 'follow_ball') {
      this._poseFollowBall();
    }
  }

  setFollowTarget(actorId, position) {
    this.followTargetActorId = actorId || null;
    if (position && typeof position.x === 'number') {
      this.followTargetPosition.set(position.x, 0, position.z ?? position.y ?? 0);
    }
    if (this.activeCameraId === 'follow_player') {
      this._poseFollowPlayer();
    }
  }

  /**
   * Called by FootballScene3D every frame so chase cameras track in-flight
   * tweens without waiting for the next event boundary.
   */
  tick(playerGroups) {
    if (this.activeCameraId === 'follow_player') {
      const group = this.followTargetActorId && playerGroups?.[this.followTargetActorId];
      if (group) {
        this.followTargetPosition.copy(group.position);
      }
      this._poseFollowPlayer();
    } else if (this.activeCameraId === 'follow_ball') {
      // Ball position is updated by setQBPosition / setBallPosition,
      // but call the pose every frame so the camera lags smoothly.
      this._poseFollowBall();
    }
  }

  _updateForId(cameraId) {
    switch (cameraId) {
      case 'qb_perspective':
        this._poseQBFirstPerson();
        break;
      case 'sideline':
        this._poseSideline();
        break;
      case 'broadcast':
        this._poseBroadcast();
        break;
      case 'end_zone':
        this._poseEndZone();
        break;
      case 'follow_ball':
        this._poseFollowBall();
        break;
      case 'follow_player':
        this._poseFollowPlayer();
        break;
      case 'top_down':
      default:
        break;
    }
  }

  _poseQBFirstPerson() {
    const eyeHeight = 1.85;
    this.perspective.fov = 75;
    this.perspective.position.set(
      this.qbPosition.x - 2.0,
      eyeHeight,
      this.qbPosition.z,
    );
    this.perspective.lookAt(
      this.vp.width,
      1.2,
      this.qbPosition.z,
    );
    this.perspective.updateProjectionMatrix();
  }

  _poseSideline() {
    const coachHeight = 2.8;
    this.perspective.fov = 55;
    this.perspective.position.set(
      this.vp.width / 2,
      coachHeight,
      -8,
    );
    this.perspective.lookAt(this.vp.width / 2, 1.0, this.vp.height / 2);
    this.perspective.updateProjectionMatrix();
  }

  _poseBroadcast() {
    this.perspective.fov = 50;
    this.perspective.position.set(
      -10,
      32,
      this.vp.height / 2 - 22,
    );
    this.perspective.lookAt(this.vp.width / 2 + 10, 0, this.vp.height / 2 + 4);
    this.perspective.updateProjectionMatrix();
  }

  _poseEndZone() {
    this.perspective.fov = 65;
    this.perspective.position.set(
      -4,
      6,
      this.vp.height / 2,
    );
    this.perspective.lookAt(this.vp.width, 1.5, this.vp.height / 2);
    this.perspective.updateProjectionMatrix();
  }

  _poseFollowBall() {
    const cfg = this.followConfig;
    this.perspective.fov = 60;
    this.perspective.position.set(
      this.ballPosition.x - cfg.distance_m,
      cfg.height_m,
      this.ballPosition.z,
    );
    this.perspective.lookAt(
      this.ballPosition.x + 2.0,
      1.0,
      this.ballPosition.z,
    );
    this.perspective.updateProjectionMatrix();
  }

  _poseFollowPlayer() {
    const cfg = this.followConfig;
    const target = this.followTargetActorId ? this.followTargetPosition : this.qbPosition;
    this.perspective.fov = 60;
    this.perspective.position.set(
      target.x - cfg.distance_m,
      cfg.height_m,
      target.z,
    );
    this.perspective.lookAt(target.x + 2.0, 1.0, target.z);
    this.perspective.updateProjectionMatrix();
  }

  resize(aspect) {
    this.aspect = aspect;
    const { halfW, halfH } = this._orthoFrustum(aspect);
    this.ortho.left = -halfW;
    this.ortho.right = halfW;
    this.ortho.top = halfH;
    this.ortho.bottom = -halfH;
    this.ortho.updateProjectionMatrix();

    this.perspective.aspect = aspect;
    this.perspective.updateProjectionMatrix();
  }

  /**
   * Hot-swap orientation without tearing down the WebGL scene. Called by
   * ThreeGame whenever the resolved viewport orientation changes — coach-
   * app boots in portrait, learner-app in landscape, and either app can
   * flip via the user toggle / "view as learner" preview.
   *
   * The field mesh, actors, and animations stay put in canonical world
   * coords; we only rotate the orthographic camera's `up` vector and
   * swap the long/short axes of the frustum. Perspective cameras (QB,
   * sideline, broadcast, end-zone, follow_*) are unaffected since their
   * pose is meaningful regardless of orientation — they just render in
   * a tall narrow window if the container is portrait.
   */
  setOrientation(orientation) {
    const next = _VALID_ORIENTATIONS.has(orientation) ? orientation : 'landscape';
    if (next === this.orientation) return;
    this.orientation = next;
    this._applyOrthoUp();
    const { halfW, halfH } = this._orthoFrustum(this.aspect);
    this.ortho.left = -halfW;
    this.ortho.right = halfW;
    this.ortho.top = halfH;
    this.ortho.bottom = -halfH;
    this.ortho.updateProjectionMatrix();
  }

  dispose() {
    // cameras don't hold GPU resources
  }
}
